// frontend/src/hooks/useAnonymousCredits.ts
//
// Hook for fetching anonymous (unauthenticated) user credits.
// Returns the remaining daily credits tracked by IP on the server.

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface UseAnonymousCreditsReturn {
  credits: number | null;
  loading: boolean;
  refetch: () => Promise<void>;
}

// Simple cache to avoid excessive calls
let cachedCredits: number | null = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 30 * 1000; // 30 seconds
let invalidationVersion = 0;

export function useAnonymousCredits(): UseAnonymousCreditsReturn {
  const { user } = useAuth();
  const [credits, setCredits] = useState<number | null>(cachedCredits);
  const [loading, setLoading] = useState(cachedCredits === null);
  const [version, setVersion] = useState(invalidationVersion);

  // Poll for cache invalidation so all hook instances refresh together.
  useEffect(() => {
    const interval = setInterval(() => {
      if (invalidationVersion !== version) {
        setVersion(invalidationVersion);
      }
    }, 200);
    return () => clearInterval(interval);
  }, [version]);

  const fetchCredits = useCallback(async (force = false) => {
    // Don't fetch if user is authenticated — useUsage handles that
    if (user) {
      setCredits(null);
      setLoading(false);
      return;
    }

    if (!force && cachedCredits !== null && Date.now() - cacheTimestamp < CACHE_DURATION) {
      setCredits(cachedCredits);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-anonymous-credits`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch anonymous credits');
      }

      const data = await response.json();
      const remaining = data.credits_remaining ?? null;

      cachedCredits = remaining;
      cacheTimestamp = Date.now();

      setCredits(remaining);
    } catch (err) {
      console.error('Error fetching anonymous credits:', err);
      // Don't overwrite existing cached value on error
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchCredits();
  }, [fetchCredits, version]);

  const refetch = useCallback(async () => {
    await fetchCredits(true);
  }, [fetchCredits]);

  return { credits, loading, refetch };
}

/** Invalidate the anonymous credits cache (e.g. after a run completes) */
export function invalidateAnonymousCreditsCache() {
  cachedCredits = null;
  cacheTimestamp = 0;
  invalidationVersion++;
}
